import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Dialog } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import LoyaltyPointForm from './LoyaltyPointForm';
import { closeLoyaltyPointsDialog, selectLoyaltyPointsDialog } from '../../stateManagement/loyaltyPointsDialogSlice';
import { AppDispatch } from '../../stateManagement/store';
import { ILoyaltyPoint } from "@ontime/types";
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import GridTable, { GridColumnType } from '../../gridTable/GridTable';
import { deleteLoyaltyPointAsync, fetchLoyaltyPoints, selectLoyaltyPoints, selectLoyaltyPointsError, selectLoyaltyPointsLoading } from '../../stateManagement/loyaltyPointSlice';
import { useTheme, useMediaQuery } from '@mui/material';

const LoyaltyPointListDialog: React.FC = () => {

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const loyaltyPointsDialog = useSelector(selectLoyaltyPointsDialog);
    const organization = useSelector(selectSelectedOrganization);

    const loyaltyPoint = useSelector(selectLoyaltyPoints);
    const loading = useSelector(selectLoyaltyPointsLoading);
    const error = useSelector(selectLoyaltyPointsError);

    const { enqueueSnackbar } = useSnackbar();

    const dispatch = useDispatch<AppDispatch>();

    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const onClickDeleteFolder = () => {
        setShowDeleted(!showDeleted);
    }

    const [formOpen, setFormOpen] = useState<{ open: boolean; loyaltyPoint?: ILoyaltyPoint | null }>({ open: false });

    const closeForm = () => {
        setFormOpen({ open: false });
    };

    const [rowLoading, setRowLoading] = useState<string[]>([]);

    const handleDelete = async (input: ILoyaltyPoint) => {
        if (input.organizationId) {
            setRowLoading([...rowLoading, input.loyaltyPointId]);
            dispatch(deleteLoyaltyPointAsync({
                organizationId: input?.organizationId ?? "",
                loyaltyPointId: input.loyaltyPointId,
                currentIsDeleted: input.isDeleted ?? false,
            }))
                .unwrap()
                .catch((error) => {
                    console.error(error, { variant: 'error' });
                })
                .finally(() => {
                    setRowLoading(rowLoading.filter((item) => item !== input.loyaltyPointId))
                });
        }
    };

    const loyaltyPointType: GridColumnType<ILoyaltyPoint> = {
        loyaltyPointId: { type: "Id", dontShow: true },
        organizationId: { type: "Integer", dontShow: true },
        startDate: { type: "Date", header: 'Start Date', },
        endDate: { type: "Date", header: 'End Date' },
        referralPoints: { type: "Price", header: 'Referral points' },
        usedPointsCount: { type: "Price", header: 'Used points count' },
        usedAmount: { type: "Price", header: 'Used amount' },
        earnedPointsCount: { type: "Price", header: 'Earned points count' },
        earnedAmount: { type: "Price", header: 'Earned amount' },
    };

    const handleNew = () => {
        setFormOpen({ open: true })
    };

    const handleEdit = (input: ILoyaltyPoint) => {
        setFormOpen({ open: true, loyaltyPoint: input });
    };

    const handleClose = () => {
        setShowDeleted(false);
        dispatch(closeLoyaltyPointsDialog());
    };

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error);
        }
    }, [error]);

    useEffect(() => {
        if (organization && organization.organizationId) {
            dispatch(fetchLoyaltyPoints({ organizationId: organization.organizationId, showDeleted }));
        }
    }, [dispatch, organization, showDeleted]);

    return (
        <Dialog
            open={loyaltyPointsDialog.open}
            onClose={() => handleClose()}
            fullWidth
            fullScreen={fullScreen} >
            {formOpen.open ?
                <LoyaltyPointForm loyaltyPointInput={formOpen.loyaltyPoint} closeForm={closeForm} />
                :
                <GridTable
                    gridTitle='Place loyalty rules list'
                    gridTitleInfo="Define and manage the loyalty point rules for this place, including how clients earn and redeem points."
                    loading={loading}
                    list={loyaltyPoint.map((item) => ({ ...item, id: item.loyaltyPointId }))}
                    inputType={loyaltyPointType}
                    onClickNew={handleNew}
                    onEditRow={handleEdit}
                    onDeleteRow={handleDelete}
                    deleteLoading={rowLoading}
                    onClickBack={handleClose}
                    isOnDialog={true}
                    showInfoToggleButton={true}
                    onClickDeleteFolder={onClickDeleteFolder}
                    showDeleted={showDeleted}
                />
            }
        </Dialog>
    );
};

export default LoyaltyPointListDialog;
